// packages/frontend/src/components/Header.tsx
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import { AppBar, Box, Button, Collapse, Container, IconButton, Toolbar, Typography } from '@mui/material';
import Badge from '@mui/material/Badge';
import { getAuth, signOut } from 'firebase/auth';

import { makeAuthenticatedApiCall } from '../apiHelper';
import { useCofinancementCounts } from '../hooks/useApiQueries';
import { useLeadsVoAgent } from '../hooks/useLeadsVoQueries';
import useFromExternalStore from '../stores/FromExternalStore';
import theme from '../theme';
import skipcarLogoWhite from '../assets/skipcar_logo_white.svg';
import AdminDrawer from './AdminDrawer';
import { useAuth } from './AuthContext';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [dealsInfo, setDealsInfo] = useState({ toBeTreated: 0, awaitingSupervision: 0 });
  const [mobileSubMenus, setMobileSubMenus] = useState<{ [key: string]: boolean }>({});

  const location = useLocation();
  const { isAuthenticated, currentUser, isInitializing } = useAuth();
  const userRole = currentUser?.data?.data?.role;

  const isFromExternal = useFromExternalStore((state) => state.fromExternal);
  const setFromExternal = useFromExternalStore((state) => state.setFromExternal);

  // L'agence ne voit le menu RDV Qualifiés que si elle est activée (le endpoint renvoie 403 sinon)
  const { isSuccess: leadsVoEnabled } = useLeadsVoAgent();

  const navigate = useNavigate();
  const { t } = useTranslation('common');

  useEffect(() => {
    if (!isInitializing && isAuthenticated && !isFromExternal && userRole !== 'agency') {
      const fetchCounts = async () => {
        try {
          const [toBeTreatedResponse, awaitingSupervisionResponse] = await Promise.all([
            makeAuthenticatedApiCall('get', '/api/arbitration/count/to-be-treated'),
            makeAuthenticatedApiCall('get', '/api/arbitration/count/awaiting-supervision'),
          ]);

          setDealsInfo({
            toBeTreated: toBeTreatedResponse.data as number,
            awaitingSupervision: awaitingSupervisionResponse.data as number,
          });
        } catch (error) {
          console.error('Error fetching deal counts:', error);
        }
      };
      fetchCounts();
    }
  }, [isInitializing, isAuthenticated, userRole, isFromExternal]);

  // Utiliser le hook pour les compteurs de cofinancement (sans cache, données en temps réel)
  const shouldFetchCofinancementCounts =
    !isInitializing && isAuthenticated && !isFromExternal && (userRole === 'arbitrator' || userRole === 'god');
  const { data: cofinancementCountsData } = useCofinancementCounts(shouldFetchCofinancementCounts);
  const cofinancementCounts = cofinancementCountsData || { pending: 0, client_validated: 0 };

  const menuItems = isFromExternal
    ? []
    : [
        { label: 'Accueil', path: '/' },
        ...(userRole === 'agency'
          ? [
              { label: 'Affaires en cours', path: '/reprise' },
              { label: 'Demande de cotation', path: '/cotation' },
              ...(leadsVoEnabled
                ? [
                    {
                      label: 'RDV Qualifiés',
                      path: '#',
                      subItems: [
                        { label: 'Mes Packs', path: '/leads-vo/packs' },
                        { label: 'Mes RDV', path: '/leads-vo/rdvs' },
                        { label: 'Mes critères', path: '/leads-vo/settings' },
                        { label: 'Mon calendrier', path: '/leads-vo/calendrier' },
                      ],
                    },
                  ]
                : []),
            ]
          : [
              {
                label: 'Reprises',
                path: '#',
                subItems: [
                  { label: 'Demande de cotation', path: '/cotation' },
                  { label: 'Affaires en cours', path: '/deals-list' },
                ],
              },
              {
                label: 'RDV Qualifiés',
                path: '#',
                subItems: [
                  { label: 'Dashboard', path: '/leads-vo/admin/dashboard' },
                  { label: 'Agents', path: '/leads-vo/admin/agents' },
                  { label: 'Packs', path: '/leads-vo/admin/packs' },
                ],
              },
            ]),
        { label: 'À arbitrer', path: '/arbitrator' },
        ...(userRole === 'arbitrator' || userRole === 'god'
          ? [{ label: 'Skipcar+', path: '/skipcar-plus' }]
          : []),
        {
          label: 'Carte grise',
          path: '#',
          subItems: [
            {
              label: 'Demande de CG',
              path: '/form-cg',
              // "Nouvelle demande" → session FormCGIA v2 vierge SANS annuler
              // l'analyse en cours : celle-ci continue en arrière-plan dans le
              // tracker. FormCGIAv2 consomme cette clé au mount/navigation et
              // déclenche handleNewSession (snapshot + reset, pas de cancel).
              onClick: () => {
                sessionStorage.setItem('form_cgia_new_session', '1');
              },
            },
            { label: 'Liste des CG', path: '/list-cg' },
          ],
        },
        {
          label: "Déclaration d'achat",
          path: '#',
          subItems:
            userRole === 'agency'
              ? [
                  { label: 'Demande de DA', path: '/form-da' },
                  { label: 'Liste des DA', path: '/list-da' },
                ]
              : [
                  { label: 'Demande de DA', path: '/form-da' },
                  { label: 'Liste des DA', path: '/list-da' },
                ],
        },
      ];

  const toggleMobileSubMenu = (menuLabel: string) => {
    setMobileSubMenus((prev) => ({
      ...prev,
      [menuLabel]: !prev[menuLabel],
    }));
  };

  const handleSignOut = () => {
    const auth = getAuth();
    signOut(auth)
      .then(() => {
        if (isFromExternal) {
          setFromExternal(false);
          navigate('/login', { replace: true });
        } else {
          navigate('/login', { replace: true });
        }
      })
      .catch((error) => {
        console.error('Error signing out: ', error);
      });
  };

  return (
    <>
      <AppBar position="static" sx={{ backgroundColor: 'black', background: 'black' }}>
        <Container maxWidth={false}>
          <Toolbar disableGutters>
            {!isFromExternal && (
              <RouterLink to="/">
                <img
                  src={skipcarLogoWhite}
                  alt="Skipcar"
                  className="logoSkipcar"
                  style={{ height: '40px', marginTop: '8px' }}
                />
              </RouterLink>
            )}
            {isFromExternal && (
              <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
                <RouterLink to="https://skipcar.fr">
                  <img
                    src={skipcarLogoWhite}
                    alt="Skipcar"
                    className="logoSkipcar"
                    style={{ height: '40px', marginTop: '8px' }}
                  />
                </RouterLink>
              </Box>
            )}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
              {isAuthenticated &&
                !isFromExternal &&
                menuItems.map((page, index) => {
                  const badgeContent =
                    userRole === 'arbitrator'
                      ? page.path === '/arbitrator'
                        ? dealsInfo.toBeTreated
                        : page.path === '/skipcar-plus'
                          ? cofinancementCounts.client_validated || 0
                          : 0
                      : userRole === 'god'
                        ? page.path === '/arbitrator'
                          ? dealsInfo.awaitingSupervision
                          : page.path === '/skipcar-plus'
                            ? cofinancementCounts.client_validated || 0
                            : 0
                        : 0;

                  if (index === 0) {
                    // en desktop on affiche pas accueil
                    return null;
                  }
                  if (
                    (page.path === '/companies-list' ||
                      page.path === '/arbitrator' ||
                      page.path === '/marketplace' ||
                      page.path === '/accounts') &&
                    userRole === 'agency'
                  ) {
                    return null; // Ne pas afficher l'élément pour les utilisateurs ayant le rôle "agency"
                  }

                  // Handle dropdown menus with CSS
                  if (page.subItems) {
                    return (
                      <Box key={index} className="dropdown-container" sx={{ display: 'flex', alignItems: 'center' }}>
                        <Badge
                          sx={{ '.MuiBadge-badge': { top: 20, right: 20 } }}
                          badgeContent={badgeContent}
                          color="error"
                        >
                          <button className="dropdown-button">
                            <Typography
                              variant="body2"
                              color={'#fdd15f'}
                              className="menuElement"
                              textAlign="center"
                              component="span"
                              sx={{ fontSize: '16px !important', fontWeight: '600 !important' }}
                            >
                              {page.label}
                            </Typography>
                            <ExpandMoreIcon className="dropdown-arrow" sx={{ color: '#fdd15f', fontSize: '1rem' }} />
                          </button>
                        </Badge>
                        <div className="dropdown-menu">
                          {page.subItems.map((subItem, subIndex) => (
                            <RouterLink
                              key={subIndex}
                              to={subItem.path}
                              className="dropdown-item"
                              onClick={(subItem as any).onClick}
                            >
                              {subItem.label}
                            </RouterLink>
                          ))}
                        </div>
                      </Box>
                    );
                  }

                  return (
                    <Box key={index}>
                      <Badge
                        sx={{ '.MuiBadge-badge': { top: 20, right: 20 } }}
                        badgeContent={badgeContent}
                        color="error"
                      >
                        <Button
                          component={RouterLink}
                          to={page.path}
                          sx={{ my: 2, color: theme.palette.black.main, display: 'block' }}
                        >
                          <Typography
                            variant="body2"
                            color={'#fdd15f'}
                            className={location.pathname === page.path ? 'menuElement active' : 'menuElement'}
                            textAlign="center"
                          >
                            {page.label}
                          </Typography>
                        </Button>
                      </Badge>
                    </Box>
                  );
                })}
            </Box>
            {isFromExternal && (
              <Button
                variant="contained"
                sx={{
                  width: 200,
                  display: { xs: 'none', md: 'flex' },
                  backgroundColor: theme.palette.red.main,
                  '&:hover': { backgroundColor: theme.palette.saffron.main },
                  color: 'white',
                }}
                onClick={() => {
                  handleSignOut();
                  setFromExternal(false);
                  navigate('/login', { replace: true });
                }}
              >
                J'ai déjà un compte
              </Button>
            )}
            {isAuthenticated && !isFromExternal && (
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0 }}>
                <Button
                  variant="contained"
                  sx={{
                    width: 180,
                    textTransform: 'none',
                    backgroundColor: theme.palette.red.main,
                    '&:hover': { backgroundColor: theme.palette.saffron.main },
                    color: 'black !important',
                    fontWeight: '600 !important',
                  }}
                  onClick={handleSignOut}
                >
                  {t('Sign out')}
                </Button>
                {userRole === 'god' && <AdminDrawer />}
              </Box>
            )}
            <Box sx={{ flexGrow: 1, justifyContent: 'flex-end', display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                edge="start"
                sx={{ color: 'white' }}
                aria-label="menu"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <CloseIcon style={{ color: 'white' }} /> : <MenuIcon style={{ color: 'white' }} />}
              </IconButton>
            </Box>
            <Button
              id="test_sentry"
              variant="contained"
              sx={{ backgroundColor: theme.palette.red.main, color: 'white', display: 'none', width: 256 }}
            >
              {t('Test Sentry')}
            </Button>
          </Toolbar>
        </Container>
      </AppBar>
      {mobileMenuOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 56,
            left: 0,
            maxWidth: '100%',
            height: 'calc(100% - 56px)',
            background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
            display: { xs: 'flex', md: 'none' },
            alignItems: 'flex-start',
            justifyContent: 'center',
            flexDirection: 'column',
            zIndex: 1300,
            overflowY: 'auto',
            padding: '20px',
          }}
        >
          {isAuthenticated &&
            !isFromExternal &&
            menuItems.map((page, index) => {
              const badgeContent =
                userRole === 'arbitrator'
                  ? page.path === '/arbitrator'
                    ? dealsInfo.toBeTreated
                    : page.path === '/skipcar-plus'
                      ? cofinancementCounts.client_validated || 0
                      : 0
                  : userRole === 'god'
                    ? page.path === '/arbitrator'
                      ? dealsInfo.awaitingSupervision
                      : page.path === '/skipcar-plus'
                        ? cofinancementCounts.client_validated || 0
                        : 0
                    : 0;

              if (page.label === 'Liste des agences' && userRole === 'agency') {
                return null;
              }

              if (page.subItems) {
                return (
                  <Box key={index} sx={{ maxWidth: '100%', width: '100%', mb: 1 }}>
                    <Button
                      fullWidth
                      onClick={() => toggleMobileSubMenu(page.label)}
                      sx={{
                        justifyContent: 'space-between',
                        color: '#fdd15f',
                        backgroundColor: 'rgba(253, 209, 95, 0.1)',
                        border: '1px solid rgba(253, 209, 95, 0.3)',
                        borderRadius: '8px',
                        py: 1.5,
                        mb: 1,
                        '&:hover': {
                          backgroundColor: 'rgba(253, 209, 95, 0.2)',
                        },
                      }}
                    >
                      <Typography variant="body1" color="#fdd15f" fontWeight="500">
                        {page.label}
                      </Typography>
                      <ExpandMoreIcon
                        sx={{
                          color: '#fdd15f',
                          transform: mobileSubMenus[page.label] ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s ease',
                        }}
                      />
                    </Button>
                    <Collapse in={mobileSubMenus[page.label]}>
                      <Box sx={{ pl: 2, pb: 2 }}>
                        {page.subItems.map((subItem, subIndex) => (
                          <Button
                            key={subIndex}
                            fullWidth
                            component={RouterLink}
                            to={subItem.path}
                            onClick={() => {
                              (subItem as any).onClick?.();
                              setMobileMenuOpen(false);
                            }}
                            sx={{
                              justifyContent: 'flex-start',
                              color: '#fdd15f',
                              backgroundColor: 'rgba(0, 0, 0, 0.3)',
                              border: '1px solid rgba(253, 209, 95, 0.2)',
                              borderRadius: '6px',
                              py: 1,
                              mb: 1,
                              ml: 1,
                              '&:hover': {
                                backgroundColor: 'rgba(253, 209, 95, 0.1)',
                              },
                            }}
                          >
                            <Typography variant="body2" color="#fdd15f">
                              {subItem.label}
                            </Typography>
                          </Button>
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                );
              }

              return (
                <Box key={index} sx={{ width: '100%', mb: 1 }}>
                  <Badge badgeContent={badgeContent} color="error">
                    <Button
                      fullWidth
                      component={RouterLink}
                      to={page.path}
                      onClick={() => setMobileMenuOpen(false)}
                      sx={{
                        justifyContent: 'center',
                        color: '#fdd15f',
                        backgroundColor:
                          location.pathname === page.path ? 'rgba(253, 209, 95, 0.2)' : 'rgba(253, 209, 95, 0.1)',
                        border: '1px solid rgba(253, 209, 95, 0.3)',
                        borderRadius: '8px',
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: 'rgba(253, 209, 95, 0.2)',
                        },
                      }}
                    >
                      <Typography variant="body1" color="#fdd15f" fontWeight="500">
                        {page.label}
                      </Typography>
                    </Button>
                  </Badge>
                </Box>
              );
            })}
          <Box sx={{ width: '100%', mt: 3 }}>
            <Button
              fullWidth
              variant="contained"
              sx={{
                backgroundColor: theme.palette.red.main,
                '&:hover': { backgroundColor: theme.palette.saffron.main },
                color: 'black !important',
                fontWeight: '600 !important',
                borderRadius: '8px',
                py: 1.5,
              }}
              onClick={() => {
                handleSignOut();
                setMobileMenuOpen(false);
              }}
            >
              {t('Sign out')}
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};

export default Header;
